/**
 * fetchRegistry — module-level authorized fetch registry.
 *
 * AuthContext injects authtool's fetch (which attaches Authorization: Bearer <JWT>
 * automatically) after the user authenticates. Non-hook code (service singletons,
 * auth modules) imports getAuthorizedFetch() instead of calling window.fetch directly,
 * so they transparently use the authenticated fetch without needing React hook access.
 *
 * Per spec-auth.md: never call window.fetch directly for authenticated resources.
 */

import { buildAclContextHeaders } from './aclContext'
import { track } from './sandboxPending'
import { noWake } from './sandboxSleep'

let _authorizedFetch = window.fetch.bind(window)

export function setAuthorizedFetch(fn) {
  _authorizedFetch = fn
}

// A gate index.jsx puts in front of every authorized request (utils/aiFoundryGate.js: no AI Foundry requests for a
// person whose connection is off). Set from outside so this module imports nothing that imports it back.
let _gate = (fetchImpl) => fetchImpl

export function setRequestGate(gate) {
  _gate = typeof gate === 'function' ? gate : (fetchImpl) => fetchImpl
}

// Stable wrapper that injects the `x-mia-acl-context` header (the selected authz
// org/tenant) into every authenticated request, then delegates to the current
// authtool fetch (which adds Authorization: Bearer). Existing headers win.
const _wrappedFetch = (input, init = {}) => {
  const headers = new Headers(init.headers || {})
  for (const [key, value] of Object.entries(buildAclContextHeaders())) {
    if (!headers.has(key)) { headers.set(key, value) }
  }
  // A sandbox the person put to sleep: background reads do not wake it (sandboxSleep.js).
  if (noWake(input, init) && !headers.has('x-flow-no-wake')) { headers.set('x-flow-no-wake', '1') }
  // Requests to the sandbox are watched: one that waits tells "Waking your sandbox…" to look (sandboxPending.js).
  return track(input, _gate((url, options) => _authorizedFetch(url, options))(input, { ...init, headers }))
}

export function getAuthorizedFetch() {
  return _wrappedFetch
}
